import type { ReactNode } from 'react';
import { Container, Stack, Cluster, Split } from './Layout';
import type { ContainerWidth, SplitRatio } from './Layout';
import { CONTAINER_WIDTH_VAR } from './Layout';
import { PageHeader } from './PageHeader';
import type { PageHeaderProps } from './PageHeader';
import { WorkspaceHeader } from './Headers';
import type { WorkspaceHeaderProps } from './Headers';
import { AppFrame } from '../layout/AppFrame';

// ------------------------------------------------------------------
// PageLayouts — story 2-147sb (P10 §2.6 "Layout recipes", DOD). Replaces
// `2-147f`'s six DOCUMENTED recipes (P10 §2.6, still the literal spec every
// component below implements) with real, executable components a page can
// import. `2-147f`'s own scope explicitly stopped at documentation ("not in
// scope: migrating any specific page") — this file is what makes the recipe
// a component instead of a pattern a new page author has to hand-copy.
//
// Every layout composes:
//   1. The right header KIND for its family, per `2-147e`'s naming and
//      `Headers.tsx`'s new components (`PageHeader` for Dashboard/
//      Collection/Workflow/Administration; `WorkspaceHeader` for Workspace;
//      External hand-rolls its own per P10 §2.6, no header primitive
//      applies there).
//   2. `2-147sa`'s composition primitives (`Container`/`Stack`/`Split`/
//      `Cluster`) for its own internal structure — no bespoke `flex`/`grid`
//      className anywhere in this file (`check-layout-through-primitives-
//      ceiling.mjs` proof, this story's own session record).
//   3. A width that traces to one of `2-131`'s three `--content-*` tokens,
//      via `Container`'s own `width` prop (or, for `WorkspaceLayout`, via
//      `Layout.tsx`'s newly-exported `CONTAINER_WIDTH_VAR` map feeding
//      `AppFrame`'s `contentMaxWidth`) — never a restated literal.
//
// Not in scope (per the story's own text): migrating any real page
// (Dashboard, Sites, Templates, …) onto these — that is `2-147w*`'s job.
// This file ships the six components and demonstrates them in `/app/design`
// only.
// ------------------------------------------------------------------

// ------------------------------------------------------------------
// DashboardLayout — P10 §2.6 recipe #1: the at-a-glance landing composition.
// Width: `--content-operational` (`Container`) — the same value `AppShell`'s
// own top-level canvas already renders every shell-routed page at. Header:
// Page header (kind #2), bare. Content: a flat vertical rhythm of self-
// contained blocks (`DashboardPage.tsx`'s own `space-y-6`, now `Stack`).
// ------------------------------------------------------------------
export interface DashboardLayoutProps {
  header: PageHeaderProps;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function DashboardLayout({ header, children, className = '', 'data-testid': testId }: DashboardLayoutProps): JSX.Element {
  return (
    <Container width="operational" className={className} {...(testId ? { 'data-testid': testId } : {})}>
      <Stack gap={6}>
        <PageHeader {...header} />
        {children}
      </Stack>
    </Container>
  );
}

// ------------------------------------------------------------------
// CollectionLayout — P10 §2.6 recipe #2: search/filter/sort/select over a
// set of records. Width: same shell-default inheritance as Dashboard.
// Header: Page header (kind #2), wrapped in a flat `bg-card` surface — P10
// §1.3 names "a list-page header" as this treatment's own canonical example
// (the exact wrapper `EntityListPage.tsx` already composes; this layout is
// the same recipe named for a collection-family page that is not literally
// `EntityListPage`'s own table/rows template). `filters` reflows via
// `Cluster` at narrow widths — the filter-bar wrap P10 §2.6 documents;
// `children` is the content seam (a custom table/grid), `footer` mirrors
// `EntityListPage`'s own below-the-content pagination slot (story 2-76/G-25).
// ------------------------------------------------------------------
export interface CollectionLayoutProps {
  header: PageHeaderProps;
  filters?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function CollectionLayout({
  header,
  filters,
  children,
  footer,
  className = '',
  'data-testid': testId,
}: CollectionLayoutProps): JSX.Element {
  return (
    <Container width="operational" className={className} {...(testId ? { 'data-testid': testId } : {})}>
      <Stack gap={6}>
        <div className="rounded-hairline border border-border bg-card p-6">
          <PageHeader {...header} />
        </div>
        {filters && <Cluster gap={3}>{filters}</Cluster>}
        <div>{children}</div>
        {footer}
      </Stack>
    </Container>
  );
}

// ------------------------------------------------------------------
// WorkflowLayout — P10 §2.6 recipe #3: a record moving through an explicit
// lifecycle. Width: shell-default inheritance. Header: Page header (kind
// #2) — the resolved canonical target for all three real Workflow
// consumers (`ReportDetailPage`, `ReviewDetailPage`, `SiteDetailPage`; P10
// §2.7's own tie-break), even though none of the three is migrated onto it
// by this story. Panel: this component implements shape (a) of the
// family's own three real shapes — `RailPanel`/`VersionPanel` occasional-
// management content beside the record, via `Split` (stacks under `lg`,
// two-pane at `lg`+, the exact collapse `Split` already bundles). Shape (b)
// (two panes of the SAME record, swapping to `Tabs` under `lg` rather than
// stacking) and shape (c) (no panel at all) are materially different
// collapse strategies this one component does not force — a page needing
// shape (b) composes `Split` (ratio="equal") directly with its own `Tabs`
// fallback, or shape (c) simply omits `panel`. No dedicated panel-width
// token exists yet (P10 §2.6's own named gap — `AppFrame`'s `panelWidth`
// prop carries the same unresolved literal `'20rem'`), so `panelRatio` uses
// `Split`'s proportional `2:1` (content:panel) as the closest named ratio
// to `ReportDetailPage.tsx`'s own `[1fr,320px]` proportion, not a fixed width.
// ------------------------------------------------------------------
export interface WorkflowLayoutProps {
  header: PageHeaderProps;
  /** Occasional-management content beside the record (`RailPanel`/`VersionPanel`) — omit for a Workflow page with no side panel (shape c). */
  panel?: ReactNode;
  /** Content:panel ratio when `panel` is supplied. Defaults to `'2:1'`. */
  panelRatio?: SplitRatio;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function WorkflowLayout({
  header,
  panel,
  panelRatio = '2:1',
  children,
  className = '',
  'data-testid': testId,
}: WorkflowLayoutProps): JSX.Element {
  return (
    <Container width="operational" className={className} {...(testId ? { 'data-testid': testId } : {})}>
      <Stack gap={6}>
        <PageHeader {...header} />
        {panel ? <Split primary={children} secondary={panel} ratio={panelRatio} gap={5} /> : children}
      </Stack>
    </Container>
  );
}

// ------------------------------------------------------------------
// WorkspaceLayout — P10 §2.6 recipe #4: tool-rail → canvas → contextual-
// inspector authoring/editing. Header: Workspace header (kind #3,
// `Headers.tsx`). Structure: `AppFrame` (story 2-127's own rail/canvas/panel
// layout-slot primitive — the persistent three-column shape this family
// needs, which `2-147sa`'s own primitives deliberately do not duplicate;
// `Split`'s own header comment already argues a persistent rail is out of
// scope for a two-pane primitive). Width: `AppFrame`'s `contentMaxWidth`,
// resolved through `Layout.tsx`'s exported `CONTAINER_WIDTH_VAR` map so the
// value still traces to a `2-131` token — defaults to `'fluid'`
// (`TemplateEditorPage.tsx`'s own canvas-filling editor case).
// ------------------------------------------------------------------
export interface WorkspaceLayoutProps {
  header: WorkspaceHeaderProps;
  rail?: ReactNode;
  panel?: ReactNode;
  children: ReactNode;
  /** Canvas width clamp — one of `2-131`'s three content-width modes. Defaults to `'fluid'`. */
  width?: ContainerWidth;
  /** Forwarded to `AppFrame`'s own `padded` prop — `false` when nested inside chrome that already supplies the canvas's outer padding (`CapturePage.tsx`'s own shell-nested case). */
  padded?: boolean;
  railClassName?: string;
  panelClassName?: string;
  className?: string;
  'data-testid'?: string;
}

export function WorkspaceLayout({
  header,
  rail,
  panel,
  children,
  width = 'fluid',
  padded = true,
  railClassName,
  panelClassName,
  className = '',
  'data-testid': testId,
}: WorkspaceLayoutProps): JSX.Element {
  return (
    <Stack gap={4} className={className} {...(testId ? { 'data-testid': testId } : {})}>
      <WorkspaceHeader {...header} />
      <AppFrame
        rail={rail}
        panel={panel}
        contentMaxWidth={CONTAINER_WIDTH_VAR[width]}
        padded={padded}
        railClassName={railClassName ?? ''}
        panelClassName={panelClassName ?? ''}
      >
        {children}
      </AppFrame>
    </Stack>
  );
}

// ------------------------------------------------------------------
// AdministrationLayout — P10 §2.6 recipe #5: authenticated org/platform
// governance surfaces. Width: shell-default inheritance. Header: Page
// header (kind #2), bare — same reasoning as Dashboard (no adjacent list to
// pair the header with). Panel: `OrgAccessPage.tsx`'s own two real paired
// form/summary grids (`lg:grid-cols-[1fr_20rem]` / `lg:grid-cols-[18rem_1fr]`)
// are P10 §2.6's own named, live finding — literal, un-tokenized arbitrary
// values `check-shell-geometry.mjs` has no jurisdiction over (a page,
// outside `CLOSED_FILES`). This layout's optional `panel` slot, built on
// `Split`, is the token-sourced replacement a future administration page
// reaches for instead of repeating that arbitrary value — not a migration
// of `OrgAccessPage.tsx` itself (out of this story's scope).
// ------------------------------------------------------------------
export interface AdministrationLayoutProps {
  header: PageHeaderProps;
  panel?: ReactNode;
  panelRatio?: SplitRatio;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function AdministrationLayout({
  header,
  panel,
  panelRatio = '2:1',
  children,
  className = '',
  'data-testid': testId,
}: AdministrationLayoutProps): JSX.Element {
  return (
    <Container width="operational" className={className} {...(testId ? { 'data-testid': testId } : {})}>
      <Stack gap={6}>
        <PageHeader {...header} />
        {panel ? <Split primary={children} secondary={panel} ratio={panelRatio} gap={6} /> : children}
      </Stack>
    </Container>
  );
}

// ------------------------------------------------------------------
// ExternalLayout — P10 §2.6 recipe #6: unauthenticated or minimal-chrome
// surfaces. Width: NO shell inheritance at all (these routes render outside
// `AppShell` entirely) — defaults to `'reading'` (prose measure), the
// token-sourced replacement for `PublicLayout.tsx`'s/`SharedReportPage.tsx`'s
// still-literal `max-w-6xl`/`max-w-3xl` (P10 §2.6's own named gap; neither
// file is migrated by this story). Header: no page/workspace header kind
// applies (P10 §2.6: "each screen hand-rolls its own minimal identity row");
// the optional `header` slot exists so a caller has somewhere to put that
// row instead of reaching for a raw wrapper `div`. Responsive: single
// column, centered, no rail/panel — the simplest recipe by construction.
// ------------------------------------------------------------------
export interface ExternalLayoutProps {
  header?: ReactNode;
  children: ReactNode;
  width?: ContainerWidth;
  className?: string;
  'data-testid'?: string;
}

export function ExternalLayout({
  header,
  children,
  width = 'reading',
  className = '',
  'data-testid': testId,
}: ExternalLayoutProps): JSX.Element {
  return (
    <Container width={width} className={`py-10 ${className}`} {...(testId ? { 'data-testid': testId } : {})}>
      <Stack gap={6}>
        {header}
        {children}
      </Stack>
    </Container>
  );
}
